
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import Head from 'next/head';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';
import Layout from '../../components/Layout';
import TheartreSection from '../../components/TheatreSection/TheatreSection';
import { Namespace } from '../../enums/namespaces';
import {
  basicSEO,
  createRedirectIfNeeded,
  debounce,
  determinePluralFromNumber,
  isMobileView,
  isServer,
  Permalink,
} from '../../helpers';
import { PopularTheaterAPI, Theatre, TheatresFilters } from '../../typings/api';
import { StaticPathParams } from '../../typings/staticPaths';
import styles from './index.module.scss';
import { withForcePlanToLoggedInUnsubs } from 'context';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import DramoxImage from '../../components/DramoxImage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PopularTheatre from '../../models/PopularTheatre';
import { useTheatreRow } from '../../hooks';
import Link from 'next/link';
import { useLang } from '../../context/langContext';
import FavoriteTheatres from '../../components/FavoriteTheatres';
import DramoxSelect from '../../components/Select/Select';
import { SortTheatreType } from '../../enums/sortType';
import { getSharedApiClient } from '../../api/client';
import Swiper from 'swiper';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Country } from 'enums/country';
interface Props {
  theatres: Theatre[];
  countryCode: string;
  popularTheatres: PopularTheaterAPI;
  theatresFilters: TheatresFilters;
  defaultSelectedCountry: number;
}

interface FilterValues {
  play_genre?: number;
  play_language?: string;
  play_country?: number;
  order_by: string;
  plays_count: boolean;
  include_plays: number;
}

const DEFAULT_FILTERS: FilterValues = {
  order_by: SortTheatreType.Popularity,
  play_genre: -1,
  play_language: '',
  plays_count: true,
  include_plays: 4,
};

const Theaters: NextPage<Props> = ({
  theatres,
  countryCode,
  popularTheatres,
  theatresFilters,
  defaultSelectedCountry,
}) => {
  const { t } = useTranslation(Namespace.Theaters);
  const ref = useRef(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const popularRef = useRef<HTMLDivElement>(null);
  const { locale } = useLang();
  const [localTheatres, setLocalTheatres] = useState<Theatre[]>(theatres);
  const [filters, setFilters] = useState<FilterValues>({ ...DEFAULT_FILTERS, play_country: defaultSelectedCountry });
  const [filterScrolled, setFilterScrolled] = useState<boolean>(false);
  const [isMobileState, setMobileState] = useState(false);
  const isMobile = useRef(isMobileState);
  const filterSwiper = useRef<Swiper>();
  const [loading, setLoading] = useState(false);

  const popular = PopularTheatre.fromJSON(popularTheatres);

  useEffect(() => {
    if (theatres.length > 0 && !loading) {
      const onLoad = () => {
        setLoading(false);
      };
      const img = new Image();
      img.onload = onLoad;
      img.onerror = onLoad;
      img.src = theatres[0].poster_url;
    }
  }, [theatres, loading]);

  useTheatreRow(ref);

  useEffect(() => {
    if (!isServer()) {
      const originalPosition = filterRef.current.getBoundingClientRect().y + document.documentElement.scrollTop;
      const header = document.querySelector('header');

      const handleScrollFilter = () => {
        if (isMobile.current) {
          const scrolled = document.scrollingElement.scrollTop;
          // scrolled position + height of header
          if (scrolled + 64 > originalPosition) {
            setFilterScrolled(isMobile.current && scrolled + 64 > originalPosition);
            header.classList.add('solid-header');
          } else {
            header.classList.remove('solid-header');
            setFilterScrolled(isMobile.current && false);
          }
        } else {
          setFilterScrolled(false);
        }
      };

      const debouncedScroll = debounce(handleScrollFilter, 10);

      window.addEventListener('scroll', debouncedScroll, false);

      return () => {
        window.removeEventListener('scroll', debouncedScroll);
      };
    }
  }, [filterRef, setFilterScrolled, setMobileState, popularRef]);

  useEffect(() => {
    if (!isServer()) {
      const handleResize = () => {
        setMobileState(isMobileView());
        isMobile.current = isMobileView();
      };
      window.addEventListener('resize', handleResize, false);
      handleResize();
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useEffect(() => {
    if (isMobile.current) {
      filterSwiper.current = new Swiper(filterRef.current, {
        direction: 'horizontal',
        slidesPerView: 'auto',
        observeParents: true,
        observer: true,
        freeMode: true,
        updateOnWindowResize: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
      });
    } else {
      if (filterSwiper.current) {
        filterSwiper.current.detachEvents();
        filterSwiper.current.destroy(true, true);
        filterSwiper.current = null;
      }
    }
  }, [isMobileState]);

  const updateTheatres = useCallback(
    (query: FilterValues) => {
      const tempQuery = { ...query };
      Object.keys(tempQuery).forEach((key) => {
        if (tempQuery[key] === -1 || tempQuery[key] === '') {
          delete tempQuery[key];
        }
      });
      setLoading(true);
      getSharedApiClient()
        .getTheatres(tempQuery)
        .then((values) => {
          // custom sorting in case of SortTheatreType.Created: we want the newest performances first + related theatres
          if (tempQuery.order_by === SortTheatreType.Created) {
            values = values.sort((theatreA, theatreB) => {
              type ID = { id: number };
              function sortPlays(a: ID, b: ID) {
                return a.id > b.id ? -1 : 1;
              }

              const [playA, playB] = [theatreA, theatreB].map((t) => t.plays.sort(sortPlays)[0]);
              return playA.id > playB.id ? -1 : 1;
            });
          }
          setLocalTheatres(values);
          setLoading(false);
        });
    },
    [setLocalTheatres, getSharedApiClient, setLoading],
  );

  useEffect(() => {
    updateTheatres(filters);
  }, []);

  const onFilterChanged = useCallback(
    (newValue: Partial<FilterValues>, reset = false, index = 0) => {
      const temp = { ...filters, ...newValue };
      setFilters(temp);
      updateTheatres(temp);
      if (reset) {
        window.scrollTo(0, 0);
      }
      if (filterSwiper.current) {
        const timeout = setTimeout(() => {
          filterSwiper.current.update();
          filterSwiper.current.slideTo(index);
          clearTimeout(timeout);
        }, 50);
      }
    },
    [setFilters, filters, updateTheatres],
  );

  const hasFilters = useMemo(() => {
    if (
      filters.order_by === SortTheatreType.Name &&
      filters?.play_country === -1 &&
      filters?.play_language === '' &&
      filters.play_genre === -1
    ) {
      return false;
    }
    return true;
  }, [filters]);

  const sortValues = useMemo(
    () => [
      { value: SortTheatreType.Name, label: `${t('sort_alphabeticaly')}` },
      { value: SortTheatreType.Created, label: `${t('sort_newest')}` },
      { value: SortTheatreType.Popularity, label: `${t('sort_popular')}` },
    ],
    [],
  );

  const genresValues = useMemo(
    () =>
      [{ value: -1, label: t('filter_all') }].concat(
        theatresFilters.genres?.map((genre) => ({
          value: genre.id,
          label: genre.name,
        })),
      ),
    [theatresFilters],
  );

  const languageValues = useMemo(
    () =>
      [{ value: '', label: t('filter_all') }].concat(
        theatresFilters.languages?.map((lang) => ({
          value: lang.code,
          label: lang.title,
        })),
      ),
    [theatresFilters],
  );

  const countryValues = useMemo(
    () =>
      [{ value: -1, label: t('filter_all') }].concat(
        theatresFilters.countries?.map((country) => ({
          value: country.id,
          label: country.title,
        })),
      ),
    [theatresFilters],
  );

  const skeletonRows = Array.from({ length: 3 }, (v, i) => i);
  const skeletonSlides = Array.from({ length: 4 }, (v, i) => i);

  const theaterSkeleton = () => (
    <SkeletonTheme baseColor="#22222E" highlightColor="rgba(72, 72, 72, 0.48)">
      {skeletonRows.map((i) => (
        <li key={i} className={styles.skeletonRow}>
          <div className={styles.wrap}>
            <div className={styles.circle}>
              <Skeleton duration={2} style={{ width: '100%', height: '100%', borderRadius: 48 }} />
            </div>
            <div>
              <div className={styles.first}>
                <Skeleton duration={2} style={{ width: '100%', height: '100%', borderRadius: 3 }} />
              </div>
              <div className={styles.second}>
                <Skeleton duration={2} style={{ width: '100%', height: '100%', borderRadius: 3 }} />
              </div>
            </div>
          </div>
          <ul className={styles.slides}>
            {skeletonSlides.map((j) => (
              <li key={j}>
                <div className={styles.slide}>
                  <Skeleton duration={2} style={{ width: '100%', height: '100%', borderRadius: 6 }} />
                </div>
                <div className={styles.slideDesc}>
                  <Skeleton duration={2} style={{ width: '100%', height: '100%', borderRadius: 6 }} />
                </div>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </SkeletonTheme>
  );

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(t('seo:theatres_title'), t('seo:theatres_description'))}</Head>
      <main className={styles.theatres}>
        <h1>{t('most_popular_theatres')}</h1>
        <div ref={ref} className={['scroll-wrapper-theatre', styles.popularWrapper].join(' ')}>
          <ul className={['play-list', 'swiper-wrapper', styles.mostPopular].join(' ')}>
            {popular?.most_popular_theatres?.map((theater) => (
              <li key={theater.id} className="swiper-slide">
                <Link href={Permalink.theatre(theater, locale)}>
                  <a>
                    <DramoxImage
                      usePlaceholder={true}
                      alt={theater.name}
                      width={12000}
                      height={290}
                      src={popular.getPopularPlay(theater.id).poster_url}
                    />
                    <div className={styles.text}>
                      <span style={{ background: `url(${theater.poster_url})` }} className={styles.theaterLogo} />
                      <h3 className={styles.theaterName}>{theater.name}</h3>
                      <p className={styles.playsCount}>
                        {t(`plays_count_${determinePluralFromNumber(theater?.plays_count)}`, {
                          count: theater?.plays_count,
                        })}
                      </p>
                    </div>
                  </a>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.columns}>
          <FavoriteTheatres favoriteTheatres={popular?.this_week} />
          <div ref={popularRef} className={[styles.theatresWrapper].join(' ')}>
            <div className={styles.theatresHeader}>
              <h2>{t('dramox_theatres')}</h2>
              {hasFilters && (
                <p
                  onClick={() => {
                    onFilterChanged({ ...DEFAULT_FILTERS }, true);
                  }}
                >
                  {t('clear_filter')}
                </p>
              )}
            </div>
            <div
              ref={filterRef}
              className={['scroll-wrapper-theatre', styles.filters, filterScrolled ? styles.onScrolled : ''].join(' ')}
            >
              <ul className={'play-list swiper-wrapper'}>
                <li
                  onMouseMove={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="swiper-slide"
                >
                  <div>
                    <label htmlFor="sort">{t('sort')}</label>
                    <span>{sortValues.find((ff) => ff.value === filters.order_by).label}</span>
                    <DramoxSelect
                      className={styles.select}
                      value={filters.order_by}
                      name="sort"
                      onChange={(val) => {
                        onFilterChanged({ order_by: val }, false, 0);
                      }}
                      options={sortValues}
                    />
                  </div>
                </li>
                <li className="swiper-slide">
                  <div>
                    <label htmlFor="genre">{t('genre')}</label>
                    <span>{genresValues.find((ff) => ff.value === filters.play_genre).label}</span>
                    <DramoxSelect
                      className={styles.select}
                      value={filters.play_genre}
                      name="genre"
                      onChange={(val) => {
                        onFilterChanged({ play_genre: parseInt(val) }, false, 1);
                      }}
                      options={genresValues}
                    />
                  </div>
                </li>
                <li className="swiper-slide">
                  <div>
                    <label htmlFor="play_language">{t('language')}</label>
                    <span>{languageValues.find((ff) => ff.value === filters.play_language).label}</span>
                    <DramoxSelect
                      className={styles.select}
                      value={filters.play_language}
                      name="play_language"
                      onChange={(val) => {
                        onFilterChanged({ play_language: val }, false, 2);
                      }}
                      options={languageValues}
                    />
                  </div>
                </li>
                <li className="swiper-slide">
                  <div>
                    <label htmlFor="country">{t('country')}</label>
                    <span>{countryValues.find((ff) => ff.value === filters.play_country).label}</span>
                    <DramoxSelect
                      className={styles.select}
                      value={filters.play_country}
                      name="country"
                      onChange={(val) => {
                        onFilterChanged({ play_country: parseInt(val) }, false, 3);
                      }}
                      options={countryValues}
                    />
                  </div>
                </li>
              </ul>
            </div>

            <ul className={styles.theatres}>
              {loading && theaterSkeleton()}
              {localTheatres.length > 0 &&
                localTheatres.map((theatre, i) => <TheartreSection key={i} theatre={theatre} />)}
              {!loading && localTheatres.length === 0 && (
                <li className={styles.emptyState}>
                  <div className={styles.img}></div>
                  <p>{t('no_theatres')}</p>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onFilterChanged({ ...DEFAULT_FILTERS }, true);
                    }}
                  >
                    {t('clear_filter')}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { apiClient, locale, countryCode } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  const theatres = (
    await apiClient.getTheatres({ plays_count: true, include_plays: 4, order_by: SortTheatreType.Name })
  ).filter((t) => (t.plays_count as unknown as number) > 0);

  const popularTheatres = await apiClient.popularTheatres();
  const theatresFilters = await apiClient.theatresFilters();

  // [
  //   { value: -1, label: 'Vše' },
  //   { value: 14, label: 'Austrálie' },
  //   { value: 60, label: 'Česko' },
  //   { value: 70, label: 'Estonsko' },
  //   { value: 76, label: 'Francie' },
  //   { value: 82, label: 'Gruzie' },
  //   { value: 110, label: 'Itálie' },
  //   { value: 109, label: 'Izrael' },
  //   { value: 119, label: 'Jižní Korea' },
  //   { value: 40, label: 'Kanada' },
  //   { value: 101, label: 'Maďarsko' },
  //   { value: 83, label: 'Německo' },
  //   { value: 157, label: 'Nizozemsko' },
  //   { value: 166, label: 'Norsko' },
  //   { value: 159, label: 'Nový Zéland' },
  //   { value: 177, label: 'Polsko' },
  //   { value: 183, label: 'Rusko' },
  //   { value: 202, label: 'Slovensko' },
  //   { value: 235, label: 'Spojené království' },
  //   { value: 236, label: 'Spojené státy' },
  //   { value: 209, label: 'Španělsko' },
  //   { value: 216, label: 'Švýcarsko' },
  //   { value: 233, label: 'Ukrajina' },
  // ];

  const defaultSelectedCountry = (function () {
    switch (countryCode) {
      case Country.Czechia:
        return 60;
      case Country.Ukraine:
        return 233;
      case Country.Slovakia:
        return 202;
      case Country.Poland:
        return 177;
      default:
        return -1;
    }
  })();

  return {
    props: { theatres, countryCode, popularTheatres, theatresFilters, defaultSelectedCountry },
  };
}

export default withForcePlanToLoggedInUnsubs(Theaters);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/theatres/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  